import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  LogarithmicScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Box, Typography } from "@material-ui/core";
import {
  FALLBACK_COLORS,
  REACH_COLORS,
  fmt,
} from "../usdsBoxPlot/usdsBoxPlotHelpers";
import { benchmarkPlugin } from "./UsdsLineChart";
import { bandsPlugin, hoverGuidePlugin, toBandItems } from "./usdsLineBands";

ChartJS.register(
  LinearScale,
  LogarithmicScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend
);

/**
 * A reach drawn as a longitudinal profile: sites placed by stream mile from
 * the top of the reach, the selected year against the comparison period, the
 * tributaries entering as triangles at their confluences with the value from
 * their lowest site, and the reservoir and other landmarks as bands. A site
 * whose stream distance is an estimate is drawn hollow and named in the
 * caption under the chart.
 */

const SECONDARY_COLORS = ["#ad1457", "#f48fb1"];
const MOUTH_COLORS = ["#4e342e", "#a1887f"];

export default function UsdsProfileChart({
  reach,
  mouths = [],
  landmarks = [],
  statisticLabel,
  hideBenchmark = false,
  scale = "linear",
  height = 420,
}) {
  const reachColors = REACH_COLORS[reach.reachGroupid] ?? FALLBACK_COLORS;
  const primary = useMemo(
    () => reach.series.filter((s) => !s.isSecondary),
    [reach.series]
  );
  const secondary = useMemo(
    () => reach.series.filter((s) => s.isSecondary),
    [reach.series]
  );

  const data = useMemo(() => {
    const points = (s) =>
      reach.sites
        .map((site) => {
          const pt = s.pointsBySite.get(site.locationName);
          return pt && pt.value != null
            ? {
                x: site.mile,
                y: pt.value,
                site: site.locationName,
                n: pt.recordcount,
                estimated: site.distanceEstimated,
              }
            : null;
        })
        .filter(Boolean);

    const line = (s, colors, i, axis) => ({
      label: `${s.paramAbbrev} ${s.period}`,
      data: points(s),
      yAxisID: axis,
      borderColor: colors[i % colors.length],
      backgroundColor: colors[i % colors.length],
      /* Estimated positions are drawn hollow. */
      pointBackgroundColor: (ctx) =>
        ctx.raw?.estimated ? "#fff" : colors[i % colors.length],
      pointBorderColor: colors[i % colors.length],
      pointBorderWidth: 1.5,
      pointRadius: 4,
      pointHoverRadius: 6,
      borderDash: s.isComparison ? [6, 4] : [],
      borderWidth: 2,
      spanGaps: true,
      tension: 0,
      unitName: s.unitName,
    });

    const mouthSets = primary.map((s, i) => ({
      label: `Tributary mouths, ${s.period}`,
      data: mouths
        .map((m) => {
          const pt = m.valuesByKey.get(s.key);
          return pt
            ? {
                x: m.mile,
                y: pt.value,
                site: m.mouthLocationName,
                tributary: m.label,
                n: pt.recordcount,
                estimated: m.distanceEstimated,
              }
            : null;
        })
        .filter(Boolean),
      yAxisID: "y",
      showLine: false,
      pointStyle: "triangle",
      pointRadius: 7,
      pointHoverRadius: 9,
      borderColor: MOUTH_COLORS[i % MOUTH_COLORS.length],
      backgroundColor: MOUTH_COLORS[i % MOUTH_COLORS.length],
      pointBackgroundColor: (ctx) =>
        ctx.raw?.estimated ? "#fff" : MOUTH_COLORS[i % MOUTH_COLORS.length],
      pointBorderColor: MOUTH_COLORS[i % MOUTH_COLORS.length],
      pointBorderWidth: 1.5,
      unitName: s.unitName,
    }));

    return {
      datasets: [
        ...primary.map((s, i) => line(s, reachColors, i, "y")),
        ...secondary.map((s, i) => line(s, SECONDARY_COLORS, i, "y2")),
        ...mouthSets.filter((d) => d.data.length),
      ],
    };
  }, [primary, secondary, mouths, reach.sites, reachColors]);

  const benchmarkLines = useMemo(() => {
    const first = primary[0];
    if (hideBenchmark || !first) return [];
    return [first.benchmarkHigh, first.benchmarkLow]
      .filter((v) => v != null)
      .map((value) => ({ value, axis: "y", color: "#c62828" }));
  }, [primary, hideBenchmark]);

  const bandItems = useMemo(
    () => toBandItems(landmarks, "profile"),
    [landmarks]
  );

  const { minMile, maxMile } = reach.profile;
  const siteByMile = useMemo(
    () =>
      new Map(
        reach.sites.map((s) => [Number(s.mile.toFixed(3)), s.locationName])
      ),
    [reach.sites]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      /* Every item at the hovered x: a site shows the selected year, the
         comparison and the second parameter together. "index" would pair by
         data position and mismatch the tributary triangles, whose miles no
         mainstem site shares; a triangle therefore shows on its own. */
      interaction: { mode: "x", intersect: false },
      scales: {
        x: {
          type: "linear",
          min: Math.max(0, Math.floor(minMile) - 0.5),
          max: Math.ceil(maxMile) + 0.5,
          title: {
            display: true,
            text: `Stream mile from ${
              reach.sites[0]?.locationName ?? "the top of the reach"
            }`,
          },
          /* One tick per site, labeled with its name. */
          afterBuildTicks: (axis) => {
            axis.ticks = reach.sites.map((s) => ({ value: s.mile }));
          },
          ticks: {
            autoSkip: false,
            maxRotation: 50,
            minRotation: 50,
            callback: (value) =>
              siteByMile.get(Number(Number(value).toFixed(3))) ?? "",
          },
          grid: { color: "rgba(0,0,0,0.06)" },
        },
        x2: {
          type: "linear",
          position: "top",
          min: Math.max(0, Math.floor(minMile) - 0.5),
          max: Math.ceil(maxMile) + 0.5,
          ticks: { stepSize: 2, callback: (v) => `${v} mi` },
          grid: { drawOnChartArea: false },
        },
        y: {
          type: scale === "log" ? "logarithmic" : "linear",
          title: {
            display: true,
            text: primary[0]
              ? `${primary[0].paramAbbrev} (${primary[0].unitName})`
              : statisticLabel,
          },
          beginAtZero: scale !== "log",
        },
        ...(secondary.length
          ? {
              y2: {
                type: scale === "log" ? "logarithmic" : "linear",
                position: "right",
                title: {
                  display: true,
                  text: `${secondary[0].paramAbbrev} (${secondary[0].unitName})`,
                },
                beginAtZero: scale !== "log",
                grid: { drawOnChartArea: false },
              },
            }
          : {}),
      },
      plugins: {
        legend: {
          position: "top",
          labels: { boxWidth: 14, usePointStyle: true },
        },
        usdsBenchmarks: { lines: benchmarkLines },
        usdsBands: { items: bandItems },
        tooltip: {
          callbacks: {
            title: (items) => {
              const raw = items[0]?.raw;
              if (!raw) return "";
              const where = `${raw.site}, mile ${Number(raw.x).toFixed(1)}${
                raw.estimated ? " (position estimated)" : ""
              }`;
              return raw.tributary ? `${raw.tributary}: ${where}` : where;
            },
            label: (ctx) => {
              const ds = ctx.dataset;
              const unit =
                ds.unitName && ds.unitName !== "None" ? ` ${ds.unitName}` : "";
              const n = ctx.raw?.n;
              return `${ds.label}: ${fmt(ctx.parsed.y)}${unit}${
                n ? ` (n = ${n})` : ""
              }`;
            },
          },
        },
      },
    }),
    [
      minMile,
      maxMile,
      reach.sites,
      siteByMile,
      scale,
      primary,
      secondary,
      statisticLabel,
      benchmarkLines,
      bandItems,
    ]
  );

  const ariaLabel = useMemo(() => {
    const s = primary[0];
    if (!s) return `${reach.reachLabel} profile`;
    const pts = data.datasets[0]?.data ?? [];
    const head = `${s.paramDescrip} ${statisticLabel} along ${
      reach.reachLabel
    } by stream mile, ${reach.sites.length} sites over ${(
      maxMile - minMile
    ).toFixed(1)} miles`;
    if (pts.length < 2) return `${head}.`;
    const first = pts[0];
    const last = pts[pts.length - 1];
    return `${head}. The value ${
      last.y > first.y ? "increases" : "decreases"
    } from ${fmt(first.y)} at ${first.site} to ${fmt(last.y)} at ${last.site} ${
      s.unitName
    }.`;
  }, [primary, data, reach, statisticLabel, minMile, maxMile]);

  if (!reach.series.length) {
    return (
      <Box
        height={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="body2" color="textSecondary">
          No results for {reach.reachLabel} in this period.
        </Typography>
      </Box>
    );
  }

  return (
    <Box height={height} role="img" aria-label={ariaLabel}>
      <Line
        data={data}
        options={options}
        plugins={[bandsPlugin, benchmarkPlugin, hoverGuidePlugin]}
      />
    </Box>
  );
}
